import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MealCard } from "@/components/MealCard";
import { Loader } from "@/components/Loader";

export default function MenuPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState(["All"]);
  const [allergens, setAllergens] = useState([]);
  const [meals, setMeals] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  const initialAllergens = (searchParams.get("hide") || "")
    .split(",")
    .filter(Boolean);
  const [hidden, setHidden] = useState(initialAllergens);

  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([api.get("/categories"), api.get("/allergens")]);
        setCategories(c.data.categories || ["All"]);
        setAllergens(a.data.allergens || []);
      } catch (e) {
        toast.error("Could not load menu filters.");
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/meals?category=${encodeURIComponent(category)}`)
      .then((r) => setMeals(r.data.meals || []))
      .catch(() => toast.error("Could not load meals."))
      .finally(() => setLoading(false));
  }, [category]);

  const toggleAllergen = (a) => {
    const next = hidden.includes(a) ? hidden.filter((x) => x !== a) : [...hidden, a];
    setHidden(next);
    const sp = new URLSearchParams(searchParams);
    if (next.length) sp.set("hide", next.join(","));
    else sp.delete("hide");
    setSearchParams(sp, { replace: true });
  };

  const visible = useMemo(
    () =>
      meals.filter(
        (m) => m.is_custom_box || !m.allergens?.some((a) => hidden.includes(a))
      ),
    [meals, hidden]
  );

  const safeChildName = searchParams.get("child");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight">
            The Menu 🍱
          </h1>
          <p className="text-slate-500 mt-1">Fresh, kid-approved lunches made daily.</p>
        </div>
        {safeChildName && hidden.length > 0 && (
          <span
            data-testid="safe-menu-banner"
            className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 font-semibold px-4 py-2 rounded-2xl"
          >
            <ShieldCheck size={18} /> Safe menu for {safeChildName}
          </span>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`category-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setCategory(c)}
            className={`shrink-0 px-5 py-2.5 rounded-2xl font-display font-semibold text-sm transition-all ${
              category === c
                ? "bg-emerald-500 text-white shadow-md shadow-emerald-200"
                : "bg-white text-slate-600 border-2 border-emerald-100 hover:border-emerald-300"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Allergen filter */}
      <div className="bg-white rounded-3xl border-2 border-emerald-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <SlidersHorizontal size={18} className="text-emerald-600" />
          <span className="font-display font-semibold text-slate-800">Hide meals containing:</span>
          {hidden.length > 0 && (
            <button
              data-testid="clear-allergen-filter"
              onClick={() => {
                setHidden([]);
                const sp = new URLSearchParams(searchParams);
                sp.delete("hide");
                sp.delete("child");
                setSearchParams(sp, { replace: true });
              }}
              className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-rose-500 hover:text-rose-600"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {allergens.map((a) => {
            const active = hidden.includes(a);
            return (
              <button
                key={a}
                data-testid={`allergen-filter-${a.toLowerCase()}`}
                onClick={() => toggleAllergen(a)}
                className={`px-4 py-1.5 rounded-full font-semibold text-sm border-2 transition-all ${
                  active
                    ? "bg-rose-400 text-white border-rose-400 shadow"
                    : "bg-white text-slate-600 border-slate-200 hover:border-rose-300"
                }`}
              >
                {active ? "🚫 " : ""}
                {a}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <Loader label="Fetching meals…" />
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-slate-500 font-medium">
          No meals match your filters. Try clearing some allergens.
        </div>
      ) : (
        <div
          data-testid="menu-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {visible.map((m) => (
            <MealCard key={m.meal_id} meal={m} />
          ))}
        </div>
      )}
    </div>
  );
}
